import { motion } from "framer-motion";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Card({
  className,
  children,
  glow,
}: {
  className?: string;
  children: ReactNode;
  glow?: string;
}) {
  return (
    <div
      className={cn(
        "relative rounded-2xl border border-app bg-surface/60 backdrop-blur-xl",
        className
      )}
      style={
        glow
          ? { boxShadow: `0 12px 40px -16px ${glow}55` }
          : undefined
      }
    >
      {children}
    </div>
  );
}

export function SectionTitle({
  children,
  right,
}: {
  children: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-app-2">
        {children}
      </h3>
      {right}
    </div>
  );
}

const TONES: Record<string, string> = {
  live: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  ok: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  connected: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  done: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  running: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  training: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  syncing: "bg-sky-500/15 text-sky-500 border-sky-500/30",
  evaluating: "bg-sky-500/15 text-sky-500 border-sky-500/30",
  beta: "bg-sky-500/15 text-sky-500 border-sky-500/30",
  partial: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  queued: "bg-slate-500/15 text-app-2 border-app",
  internal: "bg-violet-500/15 text-violet-500 border-violet-500/30",
  degraded: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  drifting: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  paused: "bg-slate-500/15 text-app-2 border-app",
  error: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  missing: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  failed: "bg-rose-500/15 text-rose-500 border-rose-500/30",
  stable: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
};

export function Badge({
  tone = "queued",
  children,
  className,
}: {
  tone?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize",
        TONES[tone] ?? TONES.queued,
        className
      )}
    >
      {children}
    </span>
  );
}

export function Dot({ tone = "running" }: { tone?: string }) {
  const color =
    tone === "live" || tone === "connected" || tone === "ok" || tone === "stable"
      ? "#22c55e"
      : tone === "error" || tone === "missing" || tone === "failed"
        ? "#f43f5e"
        : tone === "syncing"
          ? "#0ea5e9"
          : "#f59e0b";
  return (
    <span className="relative flex h-2 w-2">
      <span
        className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
        style={{ background: color }}
      />
      <span
        className="relative inline-flex h-2 w-2 rounded-full"
        style={{ background: color }}
      />
    </span>
  );
}

export function Progress({
  value,
  color = "#f59e0b",
  className,
}: {
  value: number;
  color?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "h-1.5 w-full overflow-hidden rounded-full bg-app/40 bg-surface-2",
        className
      )}
    >
      <motion.div
        className="h-full rounded-full"
        style={{ background: color }}
        initial={{ width: 0 }}
        animate={{ width: `${Math.round(value * 100)}%` }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
    </div>
  );
}

export function Ring({
  value,
  size = 92,
  stroke = 8,
  color = "#f59e0b",
  label,
  sub,
}: {
  value: number; // 0..100
  size?: number;
  stroke?: number;
  color?: string;
  label?: ReactNode;
  sub?: ReactNode;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="relative inline-grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          className="text-app/30"
          strokeWidth={stroke}
          opacity={0.18}
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1, ease: "easeOut" }}
          style={{ filter: `drop-shadow(0 0 6px ${color}88)` }}
        />
      </svg>
      <div className="absolute text-center">
        <div className="text-xl font-semibold text-app tabular-nums">{label}</div>
        {sub && <div className="text-[10px] text-app-2">{sub}</div>}
      </div>
    </div>
  );
}

export function Sparkline({
  data,
  color = "#f59e0b",
  width = 120,
  height = 36,
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
}) {
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((d - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  });
  const area = `0,${height} ${pts.join(" ")} ${width},${height}`;
  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={`sg-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#sg-${color})`} />
      <polyline
        points={pts.join(" ")}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function Stat({
  label,
  value,
  delta,
  color = "#f59e0b",
}: {
  label: string;
  value: ReactNode;
  delta?: string;
  color?: string;
}) {
  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wider text-app-2">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-app tabular-nums">{value}</div>
      {delta && (
        <div className="mt-1 text-xs font-medium" style={{ color }}>
          {delta}
        </div>
      )}
    </Card>
  );
}

export function AppHeader({
  icon: Icon,
  title,
  subtitle,
  accent,
  actions,
}: {
  icon: any;
  title: string;
  subtitle: string;
  accent: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between border-b border-app px-6 py-4">
      <div className="flex items-center gap-3">
        <div
          className="grid h-10 w-10 place-items-center rounded-xl text-white shadow-lg"
          style={{ background: `linear-gradient(135deg, ${accent}, ${accent}99)` }}
        >
          <Icon size={20} />
        </div>
        <div>
          <div className="text-base font-semibold text-app leading-tight">{title}</div>
          <div className="text-xs text-app-2">{subtitle}</div>
        </div>
      </div>
      {actions}
    </div>
  );
}
